import moment from "moment";
import { useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Panel } from "../../../components/Panel";
import Spinner from "../../../components/Spinners";
import { commaSeparatedNumber } from "../../../helpers/utils";

const CustomCursor = (props: any) => {
  const { x, y, width, height } = props;
  // make width 25px
  const newWidth = 25;
  // set x in center
  const remaining = (width - (newWidth + 1)) / 2;
  return (
    <Rectangle
      fill="url(#gradient)"
      x={x + remaining}
      y={y - 5}
      width={newWidth}
      height={height + 5}
      radius={[6, 6, 0, 0]}
    />
  );
};

const CustomTooltip = ({ active, payload, label, data: rawData }: any) => {
  const data = payload?.[0]?.payload;

  if (!active || !data) return null;

  return (
    <div
      className="bg-surface-primary rounded-[8px] shadow-dropdown border border-border-secondary"
      style={{ position: "relative", zIndex: 9999 }}
    >
      <div className="text-xs text-content-tertiary mb-3 text-center bg-surface-secondary p-2">
        {data?.name}
      </div>
      <div className="space-y-2 pt-2 pb-4 pl-4 pr-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: "#9BCBD4" }}
            />
            <span className="text-xs">Impressions</span>
          </div>
          <span className="text-xs text-content-primary">
            {commaSeparatedNumber(data?.impressions, false, false)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: "#EC839D" }}
            />
            <span className="text-xs">Clicks</span>
          </div>
          <span className="text-xs text-content-primary">
            {commaSeparatedNumber(data?.clicks, false, false)}
          </span>
        </div>

        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: "#EDD6AB" }}
            />
            <span className="text-xs">Conversions</span>
          </div>
          <span className="text-xs text-content-primary">
            {commaSeparatedNumber(data?.purchases, false, false)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: "#585858" }}
            />
            <span className="text-xs">Search impr. share</span>
          </div>
          <span className="text-xs text-content-primary">
            {commaSeparatedNumber(data?.searchImpressionShare, true)}
          </span>
        </div>
      </div>
    </div>
  );
};

export const transformRevenueByChannelData = ({ data = {} }: { data: any }) => {
  const trafficByDate = data?.trafficByDate || [];

  // Sort dates
  const sortedData = [...trafficByDate].sort(
    (a: any, b: any) => moment(a.date).valueOf() - moment(b.date).valueOf()
  );

  // Create transformed data
  const transformedData = sortedData.map((item: any) => {
    return {
      name: moment(item.date).format("MMM DD"),
      date: item.date,
      clicks: item.clicks || 0,
      impressions: item.impressions || 0,
      purchases: item.purchases || 0,
      searchImpressionShare: item.searchImpressionShare || 0,
    };
  });

  return transformedData;
};

export function TrafficByDevice({ data }: { data: any }) {
  const [isLoading] = useState(false);

  const graph = useMemo(() => {
    return transformRevenueByChannelData({ data });
  }, [data]);

  return (
    <Panel
      label="Trends Over Time"
      infoTooltip={
        "This chart tracks Impressions, Clicks, Bookings, and Search Impression Share over time.<br><strong>*Search Impr. Share</strong>: this shows your share of total available impressions in the auction. A low impression share shows the importance of Brand Protection campaigns."
      }
      className="h-full flex flex-col min-h-0"
      contentClassName="h-[calc(100%-50px)] xl:flex-1 xl:min-h-0"
      style={{
        position: "relative",
        zIndex: 1,
        minHeight: "300px",
        maxHeight: "400px",
      }}
      id="trends-over-time"
    >
      {isLoading ? (
        <div className="flex justify-center items-center h-full">
          <Spinner className="!text-brand-primary" />
        </div>
      ) : (
        <div className="w-full relative pt-6 pb-0 flex justify-center items-center h-full">
          {graph.length === 0 && (
            <div className="absolute top-0 text-content-primary text-sm font-medium left-0 flex justify-center text-center flex-1 items-center w-full h-full">
              No data available
            </div>
          )}
          <ResponsiveContainer>
            <LineChart
              data={graph}
              margin={{
                top: 0,
                right: 30,
                left: 20,
                bottom: 5,
              }}
              barGap={5}
            >
              <CartesianGrid
                stroke="#DBDBDB"
                strokeDasharray="3 3"
                horizontal={true}
                vertical={false}
              />

              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                className="text-xs text-content-tertiary"
                interval={"equidistantPreserveStart"}
                tickFormatter={(value) => {
                  return value;
                }}
              />
              <YAxis
                yAxisId="impressions"
                orientation="left"
                axisLine={false}
                tickLine={false}
                hide={true}
              />
              <YAxis
                yAxisId="clicks"
                orientation="left"
                axisLine={false}
                tickLine={false}
                hide={true}
              />

              <YAxis
                yAxisId="purchases"
                orientation="left"
                axisLine={false}
                tickLine={false}
                hide={true}
              />
              <YAxis
                yAxisId="searchImpressionShare"
                orientation="left"
                axisLine={false}
                tickLine={false}
                hide={true}
              />
              <Tooltip
                content={<CustomTooltip data={data} />}
                cursor={graph.length > 0 && <CustomCursor />}
              />
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="rgba(204, 196, 255, 1)" />
                  <stop offset="100%" stopColor="white" />
                </linearGradient>
              </defs>
              <>
                <Line
                  type="monotone"
                  dot={false}
                  strokeWidth={2}
                  dataKey="impressions"
                  stroke="#9BCBD4"
                  name="Impressions"
                  yAxisId="impressions"
                />
                <Line
                  type="monotone"
                  dot={false}
                  strokeWidth={2}
                  dataKey="clicks"
                  stroke="#EC839D"
                  name="Clicks"
                  yAxisId="clicks"
                />

                <Line
                  type="monotone"
                  dot={false}
                  strokeWidth={2}
                  dataKey="purchases"
                  stroke="#EDD6AB"
                  name="Purchases / Sales"
                  yAxisId="purchases"
                />
                <Line
                  type="monotone"
                  dot={false}
                  strokeWidth={2}
                  dataKey="searchImpressionShare"
                  stroke="#585858"
                  name="Search impr. share"
                  yAxisId="searchImpressionShare"
                />
              </>
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </Panel>
  );
}
